import {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import useGetAudioAnnouncementFiles from '../../API/hooks/announcement/useGetAudioAnnouncementFiles';
import { formatSizeBytes } from '../../utils/unitsUtils';
import S from './AnnouncementStyles';
import {
  getDefaultTimeoutDate,
  getDeviceDisplayId,
  getMinDateTime,
  getStartOfToday,
} from '../Settings/AssignAnnouncement/assignAnnouncementUtils';
import { AssignAnnouncementDatePickerGlobal } from '../Settings/AssignAnnouncement/AssignAnnouncementPageStyles';
import Icon from '../../components/common/Icon/Icon';
import { IconKeys } from '../../components/common/Icon/IconKeys';
import useGetDeviceAudioQueuedStatus from '../../API/hooks/announcement/useGetDeviceAudioQueuedStatus';
import useSendAudioToDevices from '../../API/hooks/announcement/useSendAudioToDevices';
import { toast } from '../../utils/toast';
import { Button } from '../../components/ui/Button/Button';

export type AudioListItem = {
  id: string;
  name: string;
  durationSec: number | null;
  sizeFormatted: string;
  audioUrl?: string | null;
};

function PlayIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden>
      <path d="M3 2v10l8-5-8-5z" />
    </svg>
  );
}

function PauseIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden>
      <rect x="2" y="2" width="4" height="10" rx="1" />
      <rect x="8" y="2" width="4" height="10" rx="1" />
    </svg>
  );
}

function WarningIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2L1 21h22L12 2zm0 14h-1v-4h2v4h-1zm0 4h-1v-2h2v2h-1z" />
    </svg>
  );
}

const SIZE_PLACEHOLDER = '—';

export default function Annoucement({ fleetId, deviceId, toggleModal }: any): any {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);
  const [selectedAudio, setSelectedAudio] = useState('');
  const [timeoutDate, setTimeoutDate] = useState<Date>(getDefaultTimeoutDate);
  const [minDate, setMinDate] = useState(() => getStartOfToday());
  const [minTimeForToday, setMinTimeForToday] = useState(() => getMinDateTime());
  const datePickerRef = useRef<DatePicker>(null);

  const { data } = useGetAudioAnnouncementFiles(fleetId ?? '');
  const { data: deviceStatus, isLoading } = useGetDeviceAudioQueuedStatus(
    fleetId ?? '',
    deviceId ?? '',
    selectedAudio,
  );
  const apiFiles = data ?? [];

  const audioList = useMemo((): AudioListItem[] => {
    if (!apiFiles.length) return [];

    return apiFiles.map((f: any) => ({
      id: f._id,
      name: f.fileName,
      durationSec: f.durationSec ?? null,
      sizeFormatted:
        f.sizeBytes != null
          ? formatSizeBytes(f.sizeBytes)
          : SIZE_PLACEHOLDER,
      audioUrl: f.url,
    }));
  }, [apiFiles]);

  useEffect(
    () => () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    },
    [audio],
  );

  useEffect(() => {
    const id = setInterval(() => {
      setMinDate(getStartOfToday());
      setMinTimeForToday(getMinDateTime());
    }, 60_000);

    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setTimeoutDate((prev) => {
      if (prev.getTime() < minTimeForToday.getTime()) {
        return new Date(minTimeForToday);
      }
      return prev;
    });
  }, [minTimeForToday]);

  const handlePlayPause = (item: AudioListItem): void => {
    if (!item.audioUrl) return;

    if (playingId === item.id && audio) {
      audio.pause();
      setPlayingId(null);
      return;
    }

    if (audio) {
      audio.pause();
    }

    const newAudio = new Audio(item.audioUrl);
    newAudio.play();

    newAudio.onended = () => {
      setPlayingId(null);
    };

    setAudio(newAudio);
    setPlayingId(item.id);
  };
  const handleCardClick = useCallback((res: any): void => {
    setSelectedAudio(res);
  }, []);

  const isSelectedDateToday = useMemo((): boolean => {
    const today = getStartOfToday();
    const selected = new Date(timeoutDate);
    selected.setHours(0, 0, 0, 0);
    return selected.getTime() === today.getTime();
  }, [timeoutDate]);

  const filterTime = useCallback(
    (time: Date): boolean => {
      if (!isSelectedDateToday) return true;
      return time.getTime() >= minTimeForToday.getTime();
    },
    [isSelectedDateToday, minTimeForToday],
  );
  const sendMutation = useSendAudioToDevices(
    (res) => {
      const okCount = res.ok?.length ?? 0;
      const failedCount = res.failed?.length ?? 0;
      if (failedCount > 0) {
        toast.error(`${okCount} sent. ${failedCount} failed.`);
      } else {
        toast.success(okCount > 0 ? `Sent to ${okCount} device(s).` : 'Done.');
        toggleModal(false);
      }
    },
    (err) => toast.error(err ?? 'Send failed'),
  );

  const handleSend = useCallback((): void => {
    if (!deviceId) return;

    const timestampSecEpochToTimeout = Math.floor(timeoutDate.getTime() / 1000);

    sendMutation.mutate({
      fleetId,
      audioId: selectedAudio,
      timestampSecEpochToTimeout,
      devices: [
        {
          deviceId,
        },
      ],
    });
  }, [deviceId, timeoutDate, fleetId, selectedAudio]);

  console.log(fleetId, selectedAudio, timeoutDate, deviceId, 'test values');
  return (
    <>{audioList.length === 0
      ? (
        <S.EmptyMessage>No Audio files mapped to this fleet</S.EmptyMessage>
      )
      : (
        <S.CardsWrapper>
          {audioList.map((res) => {
            const durationText = res.durationSec != null
              ? `${res.durationSec.toFixed(1)} sec`
              : '—';

            return (
              <S.Card
                key={res.id}
                $isSelected={selectedAudio === res.id}
                onClick={() => handleCardClick(res.id)}
              >
                <S.Main>
                  <S.TopRow>
                    <S.PlayButton
                      type="button"
                      onClick={() => handlePlayPause(res)}
                      aria-label={playingId === res.id ? 'Pause' : 'Play'}
                    >
                      {playingId === res.id ? <PauseIcon /> : <PlayIcon />}
                    </S.PlayButton>
                  </S.TopRow>

                  <S.Content>
                    <S.FileName
                      title={res.name}
                      $isSelected={selectedAudio === res.id}
                    >
                      {res.name}
                    </S.FileName>

                    <S.Meta $isSelected={selectedAudio === res.id}>
                      {durationText} · {res.sizeFormatted}
                    </S.Meta>
                  </S.Content>
                </S.Main>
              </S.Card>
            );
          })}
        </S.CardsWrapper>
      )}
      <S.TimeoutSection aria-labelledby="timeout-label">
        <S.TimeoutLabel id="timeout-label" htmlFor="announcement-timeout">Select Timeout</S.TimeoutLabel>
        <S.DatePickerWrap className="assign-announcement-timeout">
          <S.DatePickerInputWrap>
            <DatePicker
              ref={datePickerRef}
              id="announcement-timeout"
              selected={timeoutDate}
              onChange={(date: Date | null): void => {
                if (!date) return;
                const today = getStartOfToday();
                const chosenDay = new Date(date);
                chosenDay.setHours(0, 0, 0, 0);
                if (chosenDay.getTime() === today.getTime() && date.getTime() < minTimeForToday.getTime()) {
                  setTimeoutDate(new Date(minTimeForToday));
                } else {
                  setTimeoutDate(date);
                }
              }}
              minDate={minDate}
              showTimeSelect
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="MMM d, yyyy h:mm aa"
              filterTime={filterTime}
              popperClassName="assign-announcement-timeout-popper"
              popperPlacement="bottom-start"
            />
            <S.DatePickerIconWrap
              type="button"
              onClick={() => datePickerRef.current?.setFocus()}
              aria-label="Open Calendar"
            >
              <Icon iconKey={IconKeys.DATE} />
            </S.DatePickerIconWrap>
          </S.DatePickerInputWrap>
        </S.DatePickerWrap>
      </S.TimeoutSection>
      <AssignAnnouncementDatePickerGlobal />
      {deviceStatus?.queued && (
        <S.ErrorWrapper>
          <S.ErrorMessage>
            <WarningIcon />
            An announcement is already in queue. can't send new announcement now.
          </S.ErrorMessage>
        </S.ErrorWrapper>
      )}
      <Button
        height="40px"
        style={{ marginTop: '50px' }}
        padding="10px 20px"
        onClick={handleSend}
        aria-label="Send Now"
        disabled={!selectedAudio || deviceStatus?.queued}
      >
        Send Now
      </Button>
    </>
  );
}
